import React from 'react';
import i18n from '../../../i18n';
import { OPERATOR_LABELS, DATE_OPERATOR_LABELS } from '../../../utils/filterConstants';

/**
 * Translate an operator key for display.
 * Falls back to backend OPERATOR_LABELS, then to the raw operator key.
 */
export const translateOperatorLabel = (op, columnType) => {
  if (!op) return op;
  const isDate = columnType === 'date' || columnType === 'datetime';
  const englishFallback = (isDate && DATE_OPERATOR_LABELS[op]) || OPERATOR_LABELS[op] || op;
  if (isDate) {
    return i18n.t(`filters:operators.date.${op}`, {
      defaultValue: i18n.t(`filters:operators.${op}`, { defaultValue: englishFallback }),
    });
  }
  return i18n.t(`filters:operators.${op}`, { defaultValue: englishFallback });
};

/**
 * Translate a column displayName based on entity+column.
 * Falls back to the backend-provided displayName if no translation exists.
 */
export const translateColumnDisplayName = (entity, column, fallback) => {
  if (!entity || !column) return fallback;
  return i18n.t(`filters:columns.${entity}.${column}`, { defaultValue: fallback });
};

/**
 * Translate a movement type option value (Income / Expenses / Transfer).
 */
const translateTypeOption = (value) => {
  return i18n.t(`movements:types.${String(value).toLowerCase()}`, { defaultValue: value });
};

/**
 * Translate a created_by option value (user, system, manual...).
 */
const translateCreatedByOption = (value) => {
  const slug = String(value).toLowerCase();
  return i18n.t(`filters:options.createdBy.${slug}`, { defaultValue: value });
};

/**
 * Translate an alert option value (a review-flag name) for the "Alert" filter.
 */
const translateAlertOption = (value) => {
  return i18n.t(`filters:options.alert.${value}`, { defaultValue: value });
};

/**
 * Translate the boolean options of the "Ignored" filter.
 */
const translateIgnoredOption = (value) => {
  const slug = value === true || value === 'true' ? 'yes' : 'no';
  return i18n.t(`filters:options.isIgnored.${slug}`, { defaultValue: String(value) });
};

/**
 * Translate a filter option displayName when we know how to (e.g. Movement.type).
 */
export const translateOptionDisplayName = (entity, column, value, fallback) => {
  if (entity === 'Movement' && column === 'type') return translateTypeOption(value);
  if (entity === 'Movement' && column === 'created_by') return translateCreatedByOption(value);
  if (entity === 'Movement' && column === 'alert') return translateAlertOption(value);
  if (entity === 'Movement' && column === 'is_ignored') return translateIgnoredOption(value);
  return fallback;
};

/**
 * Truncate a string in the middle: "LongFileName_12345.pdf" → "LongFi…5.pdf"
 */
const truncateMiddle = (str, maxLen = 20) => {
  if (!str || str.length <= maxLen) return str;
  const keep = maxLen - 1; // 1 char for ellipsis
  const front = Math.ceil(keep / 2);
  const back = Math.floor(keep / 2);
  return `${str.slice(0, front)}…${str.slice(-back)}`;
};

/**
 * Format a date/datetime string for display
 */
export const formatDateValue = (val) => {
  if (!val || typeof val !== 'string') return String(val);
  // ISO datetime (contains T) → show YYYY-MM-DD
  if (val.includes('T')) {
    return val.split('T')[0];
  }
  return val;
};

/**
 * Format a number compactly: 1000 → "1K", 1500000 → "1.5M"
 */
const formatCompactNumber = (num) => {
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${+(num / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${+(num / 1_000).toFixed(1)}K`;
  return String(num);
};

/**
 * Format filter values for chip display.
 * - 1 value with icon: "🏦 Financial Services"
 * - Multiple values with icons: "🏦💵🛒 3 selected"
 * - Numeric: "10K", "1.5M"
 */
export const formatChipValues = (filter, filterOptions) => {
  if (filter.presetLabel) return filter.presetLabel;
  if (filterOptions && filter.values) {
    const filterDef = filterOptions.find(
      fo => fo.entity === filter.entity && fo.column === filter.column
    );
    // Numeric columns — compact number format
    if (filterDef?.columnType === 'numeric') {
      return filter.values.map(v => formatCompactNumber(Number(v))).join(', ');
    }
    // For date/datetime columns, format the values
    if (filterDef?.columnType === 'date' || filterDef?.columnType === 'datetime') {
      return filter.values.map(formatDateValue).join(', ');
    }
    if (filterDef?.options) {
      const matchedOpts = filter.values.map(v => filterDef.options.find(o => o.value === v));
      const hasIcons = matchedOpts.some(o => o?.icon);
      const resolveName = (opt, value) =>
        translateOptionDisplayName(
          filter.entity,
          filter.column,
          value,
          opt?.displayName || String(value)
        );

      if (filter.values.length === 1) {
        const opt = matchedOpts[0];
        const name = truncateMiddle(resolveName(opt, filter.values[0]));
        return opt?.icon ? <>{opt.icon} {name}</> : name;
      }

      // Multiple values — show icons + count if icons exist
      if (hasIcons) {
        const icons = matchedOpts
          .filter(o => o?.icon)
          .slice(0, 3)
          .map(o => o.icon)
          .join('');
        const label = i18n.t('filters:chip.selectedCount', { count: filter.values.length });
        return <>{icons} {label}</>;
      }

      // No icons — show names
      const names = matchedOpts.map((o, i) => truncateMiddle(resolveName(o, filter.values[i])));
      return names.length <= 3 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
    }
  }
  const vals = (filter.values || []).map(v => truncateMiddle(String(v)));
  return vals.length <= 3 ? vals.join(', ') : `${vals.slice(0, 2).join(', ')} +${vals.length - 2}`;
};

/**
 * Get display name for a filter column
 */
export const getFilterDisplayName = (filter, filterOptions) => {
  if (filterOptions) {
    const filterDef = filterOptions.find(
      fo => fo.entity === filter.entity && fo.column === filter.column
    );
    if (filterDef?.displayName) {
      return translateColumnDisplayName(filter.entity, filter.column, filterDef.displayName);
    }
  }
  return translateColumnDisplayName(filter.entity, filter.column, filter.column);
};

/**
 * Get the operator label for display in a chip
 */
export const getOperatorLabel = (filter, filterDef) => {
  return translateOperatorLabel(filter.operator, filterDef?.columnType);
};
